import { alternatePassesFilters } from "./helpers/alternateFilters";
import { ColumnNameToIndexMap, getReportColumnCount, getReportOptions, updateReportDate } from "./helpers/common";
import { addGroupingMetaData, getVMGroupings, normalizeGroupKey } from "./helpers/groupings";
import { RowFormat, createColumnNameToIndexMap, printFormattedData } from "./helpers/print";
import { formatDisplayValue } from "../utils/displayValue";
import { worksheetCache } from "../workbook/cache";
import { track } from "../../clients/mixpanel";
import { ValidationError } from "../../errors";
import { getEstimateReportData, setPrintAreaToUsedRange } from "../workbook";
import { LineItem } from "../../interfaces/LineItem";
import { User } from "../../interfaces/User";
import { columnNames, rowNames } from "./helpers/rangeNames";
import { ensureStylesExist } from "../styles/worksheet";
import { fetchEstimateExport } from "../../api/estimates";
import { fetchVMCategoriesByProjectId } from "../../api/projects";
import { fetchUsersByIds } from "../../api/admin";
import { getProcessedEstimate } from "../datasources/estimate";
import { getAlternateCreatorIds, transformAlternates } from "../datasources/alternates";

// The options that appear here are what show up on the ReportButton component

export type VMSummaryReportOptions = {
  groupByProps?: string[];
  freezeGroupLevels?: boolean;
  excludeAccepted?: boolean;
  excludeRejected?: boolean;
  groupRows?: boolean;
  onlyLevelOneSubtotals?: boolean;
  subtotalsInHeader?: boolean;
  filterByType?: string;
  addBlankRowBetweenGroups?: boolean; // Controls blank row between top-level groups
};

type VMSummaryRow = {
  row: (string | number | null)[];
  totalCost: number;
  groupValues: Record<string, string>;
};

export const vmSummaryDefaultOptions = {
  groupRows: true,
  subtotalsInHeader: true,
  freezeGroupLevels: false,
  onlyLevelOneSubtotals: false,
  excludeAccepted: false,
  excludeRejected: false,
  filterByType: "",
};

const VM_BLANK_GROUP_LABEL = "Blank";

export const fetchAlternatesWithVMData = async (estimateIdParam?: string): Promise<Record<string, any>[]> => {
  let estimateId = estimateIdParam;
  if (!estimateId) {
    const reportData = (await getEstimateReportData()) as any[];
    estimateId = reportData[0]?.estimate_id as string | undefined;
  }
  if (!estimateId) return [];

  const result = await fetchEstimateExport(estimateId);
  if (!result.success) throw result.error;
  if (!result.data) return [];

  const { alternates, projectId } = result.data;
  if (!alternates?.length) {
    return [];
  }

  const userIds = getAlternateCreatorIds(alternates);

  const [vmCategoryResult, usersResult] = await Promise.all([
    fetchVMCategoriesByProjectId(projectId),
    userIds.length > 0 ? fetchUsersByIds(userIds) : Promise.resolve({ success: true as const, data: [] as User[] }),
  ]);

  // Throw on failure — falling back to [] would print raw UUIDs for VM Category / Created By
  // (and group headers) under a success banner
  if (!vmCategoryResult.success) throw vmCategoryResult.error;
  if (!usersResult.success) throw usersResult.error;
  const vmCategories = vmCategoryResult.data ?? [];
  const users = usersResult.data ?? [];

  return transformAlternates(alternates, vmCategories, users);
};

export const generateVMSummaryReport = async (worksheet: Excel.Worksheet) => {
  const startTime = performance.now();
  const options = (await getReportOptions(worksheet, vmSummaryDefaultOptions)) as VMSummaryReportOptions;

  const reportData = (await getEstimateReportData()) as any[];
  const estimateId = reportData[0]?.estimate_id as string | undefined;
  if (!estimateId) {
    throw new ValidationError("No estimate loaded. Please load an estimate first.");
  }

  // Use the processed pipeline (parsed items with allocated markups), not the raw export:
  // raw line_items carry the DB type (not the ETL "line_item" tag) and have no `total`/
  // `total_plus_markup`, so raw-based totals compute to 0
  const processed = await getProcessedEstimate(estimateId);
  if (!processed) {
    throw new ValidationError("Failed to fetch estimate data for VM Summary report.");
  }

  const { alternates: rawAlternates, projectId } = processed.estimateData;

  if (!rawAlternates?.length) {
    throw new ValidationError("Load alternates before generating a VM Summary report.");
  }

  const userIds = getAlternateCreatorIds(rawAlternates);

  const [vmCategoryResult, usersResult] = await Promise.all([
    fetchVMCategoriesByProjectId(projectId),
    userIds.length > 0 ? fetchUsersByIds(userIds) : Promise.resolve({ success: true as const, data: [] as User[] }),
  ]);

  // Throw on failure — falling back to [] would print raw UUIDs for VM Category / Created By
  // (and group headers) under a success banner
  if (!vmCategoryResult.success) throw vmCategoryResult.error;
  if (!usersResult.success) throw usersResult.error;
  const vmCategories = vmCategoryResult.data ?? [];
  const users = usersResult.data ?? [];

  const alternates = transformAlternates(rawAlternates, vmCategories, users);

  const alternateItems = processed.alternateItems as LineItem[];

  await ensureStylesExist();

  const columnMap = await createColumnNameToIndexMap(worksheet);
  if (!options.freezeGroupLevels || !options.groupByProps?.length) {
    const groupings = await getVMGroupings(estimateId);
    options.groupByProps = groupings;
  }

  const { dataArray: vmSummaryDataArray, formatList } = vmSummaryArray(
    alternates as LineItem[],
    alternateItems,
    columnMap,
    options
  );
  const startColumnIndex = columnMap.startColumnIndex;

  await printFormattedData({
    dataArray: vmSummaryDataArray,
    formatList,
    worksheet,
    insertAtRangeName: rowNames.vmSummary,
    startColumnIndex,
    options,
    columnMap,
  });

  await addGroupingMetaData(worksheet, options.groupByProps || []);

  await worksheetCache.setOptions(worksheet, { groupByProps: options.groupByProps });

  await updateReportDate(worksheet);
  await setPrintAreaToUsedRange(worksheet);
  await worksheet.context.sync();

  track("excel.createReport.vmSummary", {
    options: options,
    loadTime: (performance.now() - startTime) / 1000,
  });
};

export const vmSummaryArray = (
  alternates: LineItem[],
  alternateItems: LineItem[],
  columnMap: ColumnNameToIndexMap,
  options: VMSummaryReportOptions
) => {
  const summaryRows = buildVMSummaryRows(alternates, alternateItems, columnMap, options);

  if (!options.groupByProps || options.groupByProps.length === 0) {
    const formatList: RowFormat[] = summaryRows.map(() => ({ style: "item", level: 0, group: [] }));
    return {
      dataArray: summaryRows.map((item) => item.row),
      formatList,
    };
  }

  return buildGroupedVMSummaryData(summaryRows, options.groupByProps, columnMap, options);
};

const buildVMSummaryRows = (
  alternates: LineItem[],
  alternateItems: LineItem[],
  columnMap: ColumnNameToIndexMap,
  options: VMSummaryReportOptions
): VMSummaryRow[] => {
  const columnCount = getReportColumnCount(columnMap);
  const visibleAlternates = alternates
    .filter((alt) => alternatePassesFilters(alt, options))
    .sort((a, b) => (a.key ?? "").localeCompare(b.key ?? "", undefined, { numeric: true }));
  const lineItems = alternateItems.filter((item) => item.type === "line_item");
  const totalCostsByAlternateId = lineItems.reduce(
    (acc, item) => {
      const alternateId = String(item.alternateId ?? "");
      acc[alternateId] = (acc[alternateId] || 0) + (Number(item.total_plus_markup) || 0);
      return acc;
    },
    {} as Record<string, number>
  );

  return visibleAlternates.map((alt) => {
    const row: (string | number | null)[] = Array(columnCount).fill(null);
    const totalCost = totalCostsByAlternateId[String(alt.id)] || 0;
    Object.entries(columnMap).forEach(([colName, colIdx]) => {
      if (colName === columnNames.totalCost) {
        row[colIdx] = totalCost;
      } else if (colName === columnNames.id) {
        row[colIdx] = alt.key;
      } else if (colName === columnNames.title) {
        row[colIdx] = alt.label;
      } else if (colName === columnNames.vmCategory) {
        row[colIdx] = alt.value_management_category;
      } else if (colName in alt) {
        row[colIdx] = alt[colName];
      }
    });
    return {
      row,
      totalCost,
      groupValues: {
        type: alt.type ?? "",
        status: alt.status ?? "",
        created_at: alt.created_at ?? "",
        created_by: alt.created_by ?? "",
        value_management_category: alt.value_management_category ?? "",
      },
    };
  });
};

const buildGroupedVMSummaryData = (
  rows: VMSummaryRow[],
  groupByProps: string[],
  columnMap: ColumnNameToIndexMap,
  options: VMSummaryReportOptions
): { dataArray: (string | number | null)[][]; formatList: RowFormat[] } => {
  const columnCount = getReportColumnCount(columnMap);
  const formatList: RowFormat[] = [];

  // Find the left-most column index for indentation (like estimate.ts)
  const indentColumn = 0;

  const groupData = (groupRows: VMSummaryRow[], level: number): (string | number | null)[][] => {
    if (level >= groupByProps.length) {
      return groupRows.map((groupRow) => {
        formatList.push({
          style: "item",
          level,
          indentColumn,
          group: Array.from({ length: level }, (_, i) => i + 1),
        });
        return groupRow.row;
      });
    }

    const groupByProp = groupByProps[level];
    const blankString = VM_BLANK_GROUP_LABEL;
    const grouped = groupRows.reduce(
      (acc, groupRow) => {
        const groupKey = normalizeGroupKey(groupRow.groupValues[groupByProp], groupByProp, blankString);
        acc[groupKey] = acc[groupKey] || [];
        acc[groupKey].push(groupRow);
        return acc;
      },
      {} as Record<string, VMSummaryRow[]>
    );

    const sortedGroupKeys = Object.keys(grouped).sort((a, b) => {
      if (a === blankString) return 1;
      if (b === blankString) return -1;
      return a.localeCompare(b);
    });

    const result: (string | number | null)[][] = [];

    sortedGroupKeys.forEach((groupKey, index) => {
      const headerRow: (string | number | null)[] = Array(columnCount).fill(null);
      headerRow[0] = formatDisplayValue(groupKey, groupByProp, VM_BLANK_GROUP_LABEL);
      result.push(headerRow);
      formatList.push({
        style: "header",
        level,
        indentColumn,
        group: level > 0 ? Array.from({ length: level }, (_, i) => i + 1) : [],
      });

      const nestedRows = groupData(grouped[groupKey], level + 1);
      result.push(...nestedRows);

      const subtotalValue = grouped[groupKey].reduce((sum, item) => sum + item.totalCost, 0);
      const totalCostColumnIndex = columnMap[columnNames.totalCost];
      const subtotalLabelColumnIndex =
        typeof totalCostColumnIndex === "number" && totalCostColumnIndex > 0 ? totalCostColumnIndex - 1 : 0;

      if (!options.onlyLevelOneSubtotals || level === 0) {
        if (options.subtotalsInHeader && typeof totalCostColumnIndex === "number") {
          headerRow[totalCostColumnIndex] = subtotalValue;
        } else if (!options.subtotalsInHeader) {
          const subtotalRow: (string | number | null)[] = Array(columnCount).fill(null);
          // Use the same formatting for subtotal label
          const subtotalLabel = formatDisplayValue(groupKey, groupByProp, VM_BLANK_GROUP_LABEL);
          subtotalRow[subtotalLabelColumnIndex] = `${subtotalLabel} Subtotal`;
          if (typeof totalCostColumnIndex === "number") {
            subtotalRow[totalCostColumnIndex] = subtotalValue;
          }
          result.push(subtotalRow);
          formatList.push({
            style: "subtotal",
            level,
            indentColumn,
            group: Array.from({ length: level + 1 }, (_, i) => i + 1),
          });
        }
      }

      // Add a blank row between top-level groups, if enabled. "" wipes the formulas filled down from
      // the first row so nothing shows on the spacer, as in estimate.ts.
      if (options.addBlankRowBetweenGroups && level === 0 && index < sortedGroupKeys.length - 1) {
        const blankRow: (string | number | null)[] = Array(columnCount).fill("");
        result.push(blankRow);
        formatList.push({
          style: "spacer",
          level,
          indentColumn,
          group: level > 0 ? Array.from({ length: level }, (_, i) => i + 1) : [],
        });
      }
    });

    return result;
  };

  const dataArray = groupData(rows, 0);
  return { dataArray, formatList };
};
